import React, { useState, useEffect } from 'react';
import { FormGroup, Label, Input, Button } from 'reactstrap';
import styled from 'styled-components';

const FormBox = styled.div`
  border: 2px solid #dc3545;
  border-radius: 8px;
  padding: 20px;
  background: linear-gradient(135deg, #ffe5e5, #ffffff);
  margin-bottom: 20px;

  h5 {
    color: #dc3545;
    margin-bottom: 15px;
    font-weight: 600;
  }
`;

const HelpText = styled.div`
  background: #f8d7da;
  border-left: 4px solid #dc3545;
  padding: 10px;
  margin-bottom: 15px;
  border-radius: 4px;
  font-size: 13px;
  color: #721c24;
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const OptionItem = styled.div<{ isWrong: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: ${props => props.isWrong ? '#ffebee' : '#f9f9f9'};
  border: ${props => props.isWrong ? '2px solid #dc3545' : '1px solid #e0e0e0'};
  border-radius: 4px;
`;

const OptionBadge = styled.span<{ isWrong: boolean }>`
  background: ${props => props.isWrong ? '#dc3545' : '#6c757d'};
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 15px;
  flex-shrink: 0;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 14px;
  margin: 0;
`;

interface FindErrorFormProps {
  formData: any;
  onChange: (data: any) => void;
}

const FindErrorForm: React.FC<FindErrorFormProps> = ({ formData, onChange }) => {
  const parseContent = () => {
    try {
      return typeof formData.content === 'string' && formData.content
        ? JSON.parse(formData.content)
        : {
            sentence: '',
            options: [],
            wrong_index: -1,
            correct_answer: '',
          };
    } catch {
      return {
        sentence: '',
        options: [],
        wrong_index: -1,
        correct_answer: '',
      };
    }
  };

  const [contentData, setContentData] = useState(parseContent());
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    onChange({
      ...formData,
      content: JSON.stringify(contentData),
    });
  }, [contentData]);

  const handleSentenceChange = (value: string) => {
    setContentData({
      ...contentData,
      sentence: value,
    });
  };

  const handleCorrectAnswerChange = (value: string) => {
    setContentData({
      ...contentData,
      correct_answer: value,
    });
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setContentData({
        ...contentData,
        options: [...contentData.options, newOption.trim()],
      });
      setNewOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    const newOptions = contentData.options.filter((_: any, i: number) => i !== index);
    const newWrongIndex = contentData.wrong_index === index ? -1 : 
                          contentData.wrong_index > index ? contentData.wrong_index - 1 : contentData.wrong_index;
    setContentData({
      ...contentData,
      options: newOptions,
      wrong_index: newWrongIndex,
    });
  };

  const handleSetWrongIndex = (index: number) => {
    setContentData({
      ...contentData,
      wrong_index: index,
    });
  };

  const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];

  return (
    <FormBox>
      <h5>🔍 Nội dung câu hỏi - Tìm lỗi sai</h5>
      
      <HelpText>
        💡 <strong>Hướng dẫn:</strong> Chia câu thành các phần (A, B, C, D...), học sinh tìm phần sai và sửa lại.
        <br/>
        Ví dụ câu: "She <strong>enjoy</strong> playing games" → Lỗi: <strong>enjoy</strong> → Sửa: <strong>enjoys</strong>
      </HelpText>

      <FormGroup>
        <Label>Câu đầy đủ: *</Label>
        <Input
          type="textarea"
          value={contentData.sentence || ''}
          onChange={(e) => handleSentenceChange(e.target.value)}
          placeholder="Nhập câu đầy đủ...

Ví dụ: She enjoy playing games every day."
          rows={3}
        />
      </FormGroup>

      <FormGroup>
        <Label>Các phần được đánh dấu (A, B, C, D...): *</Label>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
          <Input
            type="text"
            value={newOption}
            onChange={(e) => setNewOption(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddOption())}
            placeholder="Nhập từ/cụm từ cần kiểm tra..."
            style={{ flex: 1 }}
          />
          <Button color="secondary" size="sm" onClick={handleAddOption}>
            + Thêm
          </Button>
        </div>
        
        <OptionsList>
          {contentData.options.length === 0 ? (
            <div style={{ 
              padding: '20px', 
              textAlign: 'center', 
              color: '#999',
              background: '#f9f9f9',
              borderRadius: '4px'
            }}>
              Chưa có phần nào được đánh dấu
            </div>
          ) : (
            contentData.options.map((opt: string, index: number) => (
              <OptionItem key={index} isWrong={contentData.wrong_index === index}>
                <OptionBadge isWrong={contentData.wrong_index === index}>
                  {optionLabels[index]}
                </OptionBadge>
                <span style={{ flex: 1, fontWeight: contentData.wrong_index === index ? 'bold' : 'normal' }}>
                  {opt}
                </span>
                <RadioLabel>
                  <input
                    type="radio"
                    name="wrong_option"
                    checked={contentData.wrong_index === index}
                    onChange={() => handleSetWrongIndex(index)}
                  />
                  <span style={{ 
                    color: contentData.wrong_index === index ? '#dc3545' : '#999',
                    fontWeight: contentData.wrong_index === index ? 'bold' : 'normal'
                  }}>
                    {contentData.wrong_index === index ? '✕ Sai' : 'Đánh dấu sai'}
                  </span>
                </RadioLabel>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleRemoveOption(index)}
                  style={{ padding: '4px 10px', fontSize: '12px' }}
                >
                  Xóa
                </Button>
              </OptionItem>
            ))
          )}
        </OptionsList>
        <small className="form-text text-muted">
          💡 Chọn phần nào là sai, sau đó điền đáp án đúng bên dưới
        </small>
      </FormGroup>

      <FormGroup>
        <Label>Đáp án đúng (sửa lại phần sai): *</Label>
        <Input
          type="text"
          value={contentData.correct_answer || ''}
          onChange={(e) => handleCorrectAnswerChange(e.target.value)}
          placeholder="Nhập cách sửa đúng...

Ví dụ: enjoys"
        />
      </FormGroup>

      {/* Preview */}
      <div style={{ 
        background: '#ffe5e5', 
        padding: '15px', 
        borderRadius: '4px',
        marginTop: '15px'
      }}>
        <strong style={{ fontSize: '13px', color: '#c82333' }}>📋 Xem trước:</strong>
        {contentData.sentence && (
          <div style={{ 
            marginTop: '10px',
            padding: '12px',
            background: 'white',
            borderRadius: '4px',
            marginBottom: '12px'
          }}>
            <strong>Câu:</strong> {contentData.sentence}
          </div>
        )}
        {contentData.options.length > 0 && (
          <div style={{ 
            background: 'white',
            padding: '12px',
            borderRadius: '4px',
            marginBottom: '12px'
          }}>
            <strong>Các phần:</strong>
            <div style={{ marginTop: '8px' }}>
              {contentData.options.map((opt: string, index: number) => (
                <div key={index} style={{ 
                  display: 'inline-block',
                  marginRight: '12px',
                  marginBottom: '6px',
                  padding: '4px 10px',
                  background: contentData.wrong_index === index ? '#ffebee' : '#f9f9f9',
                  border: contentData.wrong_index === index ? '2px solid #dc3545' : '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontSize: '13px',
                  fontWeight: contentData.wrong_index === index ? 'bold' : 'normal'
                }}>
                  <strong>{optionLabels[index]}.</strong> {opt}
                  {contentData.wrong_index === index && ' ✕'}
                </div>
              ))}
            </div>
          </div>
        )}
        {contentData.correct_answer && (
          <div style={{ 
            background: '#d4edda',
            padding: '10px',
            borderRadius: '4px',
            border: '1px solid #28a745'
          }}>
            <strong style={{ color: '#155724' }}>✓ Đáp án đúng:</strong>
            <span style={{ marginLeft: '8px', color: '#155724' }}>
              {contentData.correct_answer}
            </span>
          </div>
        )}
      </div>

      {/* Debug JSON */}
      <details style={{ marginTop: '15px' }}>
        <summary style={{ cursor: 'pointer', color: '#666', fontSize: '14px' }}>
          🔍 Xem JSON (Debug)
        </summary>
        <pre style={{ 
          background: '#f5f5f5', 
          padding: '10px', 
          borderRadius: '4px',
          fontSize: '12px',
          maxHeight: '200px',
          overflow: 'auto',
          marginTop: '10px'
        }}>
          {JSON.stringify(contentData, null, 2)}
        </pre>
      </details>
    </FormBox>
  );
};

export default FindErrorForm;
